import React, { useState, useRef } from 'react';

/* ============================================================
   TRUCK CHECK  —  Pre-job checklist for SolarAbility
   Styled to match the Safety Hub dark theme.

   TO ADD OR EDIT A LIST: edit TEMPLATES below. That's the only
   place you need to touch. Each list has groups, each group has
   items. Keep names short so they read well on a phone.
   ============================================================ */

const TEMPLATES = {
  survey: {
    name: 'Survey w/ Poke Test',
    groups: [
      { name: 'Measure & test', items: ['Tape measure', 'Voltmeter', 'Angle finder', 'Flashlight'] },
      { name: 'Hand tools', items: ['Screwdrivers', 'Screw bit', 'Screws'] },
      {
        name: 'Poke test & drywall',
        items: [
          'Drywall bag', 'Reciprocating saw', 'Batteries (2)', 'Charger',
          'Piece of wood', 'Drywall repair', 'Heat gun', 'Transparent cover',
        ],
      },
      { name: 'Cleanup', items: ['Vacuum'] },
    ],
  },

  install: {
    name: 'Solar Install Day',
    groups: [
      { name: 'Safety & PPE', items: ['Harness & lanyard', 'Hard hats', 'Safety glasses', 'Gloves', 'Roof anchors', 'Ladders'] },
      { name: 'Racking & mounting', items: ['Rail', 'L-feet / flashing', 'Lag bolts', 'Torque wrench', 'Chalk line', 'Sealant'] },
      { name: 'Modules & electrical', items: ['PV modules (correct qty)', 'MC4 connectors + tool', 'Microinverters / optimizers', 'Wire mgmt clips', 'Rapid shutdown labels'] },
      { name: 'Tools', items: ['Drill / impact driver', 'Bit set', 'Battery packs & charger', 'Tape measure', 'Level'] },
      { name: 'Cleanup', items: ['Broom', 'Trash bags', 'Magnetic sweeper'] },
    ],
  },

  evCharger: {
    name: 'EV Charger Install',
    groups: [
      { name: 'Tools', items: ['Drill', 'Fish tape', 'Conduit bender', 'Wire strippers', 'Torque screwdriver'] },
      { name: 'Materials', items: ["Charger unit", 'Conduit & fittings', 'Wire (correct gauge)', 'Breaker', "Disconnect (if req'd)"] },
      { name: 'Finishing', items: ['Labels', 'Silicone / sealant', 'Mounting anchors'] },
    ],
  },

  oAndM: {
    name: 'O&M / Service Call',
    groups: [
      { name: 'Diagnostics', items: ['Multimeter', 'Clamp meter', 'Laptop w/ monitoring login', 'Thermal camera (if avail)', 'Assessment form ready'] },
      { name: 'Common repairs', items: ['MC4 connectors (assorted)', 'Wire nuts', 'Zip ties', 'Spare breaker (common sizes)', 'Spare microinverter / optimizer'] },
      { name: 'Roof access', items: ['Harness & lanyard', 'Ladder', 'Roof anchors'] },
      { name: 'Documentation', items: ['Phone charged', 'Tape measure (ruler ID)', 'Ruler for photo reference'] },
    ],
  },

  cleaning: {
    name: 'Panel Cleaning',
    groups: [
      { name: 'Cleaning supplies', items: ['Squeegee', 'Soft brush', 'DI water tank', 'Extension wand', 'Panel-safe soap'] },
      { name: 'Safety', items: ['Harness & lanyard', 'Ladder', 'Non-slip shoes'] },
      { name: 'Wrap-up', items: ['Before/after photos', 'Towels / rags'] },
    ],
  },

  reroof: {
    name: 'Removal & Reinstall',
    groups: [
      { name: 'Removal', items: ['Panel dolly / cart', 'ID labels (panel + wire)', 'MC4 disconnect tool', 'Hardware bins'] },
      { name: 'Storage', items: ['Padding / blankets', 'Tarps'] },
      { name: 'Reinstall', items: ['New flashing / gaskets', 'Torque wrench', 'Sealant', 'Original racking hardware'] },
    ],
  },

  // --- Add more lists here, same shape. Example to copy: ---
  // custom: {
  //   name: 'My New List',
  //   groups: [
  //     { name: 'Group name', items: ['Item 1', 'Item 2'] },
  //   ],
  // },
};

// Hub dark theme — matches App.js's C palette
const C = {
  gold: '#F5A623', goldDark: '#D4891A', goldLight: '#FFD07B',
  bg: '#0C1017', card: '#151B24', surface: '#1D2430', border: '#2A3342',
  muted: '#6E7A8A', light: '#E2E8F0', white: '#FFFFFF',
  success: '#10B981', successBg: '#0D2818',
};

// Look up a list by its key ("oAndM") or its display name ("O&M / Service Call"),
// so a project can store the human-readable name and still open the right list.
export function truckListKey(nameOrKey) {
  if (!nameOrKey) return null;
  if (TEMPLATES[nameOrKey]) return nameOrKey;
  const want = String(nameOrKey).toLowerCase();
  return Object.keys(TEMPLATES).find(k => TEMPLATES[k].name.toLowerCase() === want) || null;
}
export const TRUCK_LIST_NAMES = Object.keys(TEMPLATES).map(k => TEMPLATES[k].name);

export default function TruckCheck({ initialList, initialJob } = {}) {
  const listKeys = Object.keys(TEMPLATES);
  const [active, setActive] = useState(truckListKey(initialList) || listKeys[0]);
  const [checked, setChecked] = useState({});          // { listKey: [items] }
  const [job, setJob] = useState(initialJob || '');    // job / address, prints on the PDF
  const [tech, setTech] = useState('');                // technician name
  const printRef = useRef(null);

  const list = TEMPLATES[active];
  const allItems = list.groups.flatMap((g) => g.items);
  const onList = checked[active] || [];
  const isOn = (item) => onList.includes(item);
  const doneCount = allItems.filter(isOn).length;
  const left = allItems.length - doneCount;
  const ready = allItems.length > 0 && left === 0;

  const toggle = (item) => {
    setChecked((prev) => {
      const cur = prev[active] || [];
      const next = cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item];
      return { ...prev, [active]: next };
    });
  };

  const resetList = () => {
    if (doneCount > 0 && !window.confirm(`Uncheck all ${doneCount} items for a new job?`)) return;
    setChecked((prev) => ({ ...prev, [active]: [] }));
  };

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div style={{ background: C.bg, minHeight: '100%', paddingBottom: 24 }}>
      {/* ---------- print-only stylesheet ---------- */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #truckcheck-print, #truckcheck-print * { visibility: visible !important; }
          #truckcheck-print {
            position: absolute; left: 0; top: 0; width: 100%;
            padding: 0 !important; background: #fff !important;
          }
          @page { margin: 0.6in; }
        }
        #truckcheck-print { display: none; }
        @media print { #truckcheck-print { display: block; } }
        .tc-noprint { }
        @media print { .tc-noprint { display: none !important; } }
      `}</style>

      {/* ================= SCREEN VIEW ================= */}
      <div className="tc-noprint">
        {/* header */}
        <div style={{
          background: ready
            ? `linear-gradient(135deg, ${C.success}, #0E8F5E)`
            : `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`,
          color: C.bg,
          padding: '20px 24px 16px', borderRadius: '0 0 18px 18px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase',
                fontWeight: 800, opacity: 0.75,
              }}>
                {ready ? 'Ready to roll' : 'Truck Check'}
              </div>
              <div style={{ fontSize: 22, fontWeight: 900, marginTop: 5, lineHeight: 1.15 }}>
                {list.name}
              </div>
            </div>
            <div style={{ textAlign: 'right', flex: 'none', lineHeight: 0.9 }}>
              <div style={{ fontSize: 44, fontWeight: 900 }}>{ready ? '✓' : left}</div>
              <div style={{
                fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase',
                fontWeight: 800, opacity: 0.75,
              }}>
                {ready ? 'all loaded' : 'to load'}
              </div>
            </div>
          </div>

          <div style={{
            height: 6, background: 'rgba(12,16,23,.25)',
            borderRadius: 99, overflow: 'hidden', marginTop: 14,
          }}>
            <div style={{
              height: '100%', width: allItems.length ? `${(doneCount / allItems.length) * 100}%` : 0,
              background: C.bg, transition: 'width .3s',
            }} />
          </div>

          {listKeys.length > 1 && (
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingTop: 14 }}>
              {listKeys.map((k) => (
                <button key={k} onClick={() => setActive(k)} style={{
                  flex: 'none', border: 0, cursor: 'pointer', whiteSpace: 'nowrap',
                  padding: '7px 13px', borderRadius: 99, fontSize: 12.5, fontWeight: 700,
                  fontFamily: 'inherit',
                  background: k === active ? C.bg : 'rgba(12,16,23,.18)',
                  color: k === active ? C.gold : 'rgba(12,16,23,.65)',
                }}>
                  {TEMPLATES[k].name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* job info — feeds the printed sheet */}
        <div style={{ padding: '18px 20px 0', maxWidth: 640, margin: '0 auto' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={job} onChange={(e) => setJob(e.target.value)}
              placeholder="Job / address"
              style={inputStyle}
            />
            <input
              value={tech} onChange={(e) => setTech(e.target.value)}
              placeholder="Technician"
              style={{ ...inputStyle, maxWidth: 150 }}
            />
          </div>
        </div>

        {/* groups */}
        <div style={{ padding: '4px 20px 0', maxWidth: 640, margin: '0 auto' }}>
          {list.groups.map((g) => {
            const gDone = g.items.filter(isOn).length;
            return (
              <div key={g.name} style={{ marginTop: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '0 4px 8px' }}>
                  <span style={{
                    fontSize: 11, letterSpacing: '.15em', textTransform: 'uppercase',
                    fontWeight: 800, color: C.muted,
                  }}>{g.name}</span>
                  <span style={{ flex: 1, height: 1, background: C.border }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.muted }}>
                    {gDone}/{g.items.length}
                  </span>
                </div>
                <div style={{
                  background: C.card, border: `1px solid ${C.border}`,
                  borderRadius: 14, overflow: 'hidden',
                }}>
                  {g.items.map((item, i) => {
                    const on = isOn(item);
                    return (
                      <div
                        key={item}
                        onClick={() => toggle(item)}
                        role="checkbox"
                        aria-checked={on}
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(item); } }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 13, cursor: 'pointer',
                          padding: '15px 16px', minHeight: 58,
                          borderBottom: i === g.items.length - 1 ? 'none' : `1px solid ${C.border}`,
                          background: on ? `${C.gold}0a` : 'transparent',
                        }}
                      >
                        <span style={{
                          width: 26, height: 26, flex: 'none', borderRadius: 8,
                          border: on ? `2.5px solid ${C.gold}` : `2.5px solid ${C.border}`,
                          background: on ? C.gold : 'transparent',
                          display: 'grid', placeItems: 'center',
                        }}>
                          {on && (
                            <svg viewBox="0 0 24 24" style={{ width: 16, height: 16 }}>
                              <path d="M20 6 9 17l-5-5" fill="none" stroke={C.bg}
                                strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </span>
                        <span style={{
                          fontSize: 16, fontWeight: on ? 500 : 600, flex: 1,
                          color: on ? C.muted : C.light,
                          textDecoration: on ? 'line-through' : 'none',
                        }}>{item}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* actions */}
        <div style={{
          display: 'flex', gap: 10, padding: '24px 20px 0',
          maxWidth: 640, margin: '0 auto',
        }}>
          <button onClick={resetList} style={btnSecondary}>
            New job
          </button>
          <button onClick={() => window.print()} style={btnPrimary}>
            Print / Save PDF
          </button>
        </div>
        <p style={{
          textAlign: 'center', fontSize: 12.5, color: C.muted,
          padding: '14px 24px 24px', maxWidth: 640, margin: '0 auto',
        }}>
          Print a copy to attach to the calendar event as a backup.
        </p>
      </div>

      {/* ================= PRINT VIEW (stays light — printer-friendly) ================= */}
      <div id="truckcheck-print" ref={printRef} style={{ fontFamily: 'Helvetica, Arial, sans-serif', color: '#000' }}>
        <div style={{ borderBottom: `2px solid ${C.gold}`, paddingBottom: 8, marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 'bold' }}>SolarAbility LLC</div>
              <div style={{ fontSize: 10, color: '#555', marginTop: 2 }}>
                Solar &nbsp;•&nbsp; Storage &nbsp;•&nbsp; EV Charging
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, fontWeight: 'bold' }}>Pre-Job Truck Check</div>
              <div style={{ fontSize: 10, color: '#555', marginTop: 2 }}>{list.name}</div>
            </div>
          </div>
        </div>

        <table style={{ width: '100%', fontSize: 10.5, marginBottom: 16, borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={pCell}><b>Date:</b> {today}</td>
              <td style={pCell}><b>Job / address:</b> {job || '________________________'}</td>
              <td style={pCell}><b>Technician:</b> {tech || '__________________'}</td>
            </tr>
          </tbody>
        </table>

        {list.groups.map((g) => (
          <div key={g.name} style={{ marginBottom: 13, breakInside: 'avoid' }}>
            <div style={{
              fontSize: 10, fontWeight: 'bold', letterSpacing: '.1em',
              textTransform: 'uppercase', borderBottom: '1px solid #bbb',
              paddingBottom: 3, marginBottom: 6,
            }}>{g.name}</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {chunk(g.items, 2).map((row, ri) => (
                  <tr key={ri}>
                    {row.map((item) => (
                      <td key={item} style={{ width: '50%', padding: '3px 0', fontSize: 11.5 }}>
                        <span style={{
                          display: 'inline-block', width: 11, height: 11,
                          border: '1.2px solid #333', marginRight: 8,
                          verticalAlign: '-1px', textAlign: 'center',
                          lineHeight: '10px', fontSize: 10, fontWeight: 'bold',
                        }}>{isOn(item) ? '✓' : ''}</span>
                        {item}
                      </td>
                    ))}
                    {row.length === 1 && <td style={{ width: '50%' }} />}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        <div style={{
          marginTop: 18, borderTop: '1px solid #ccc', paddingTop: 7,
          fontSize: 9, color: '#555', display: 'flex', justifyContent: 'space-between',
        }}>
          <span>SolarAbility LLC — NABCEP Certified — ICC #21-0505</span>
          <span>Loaded by: ____________________  Time: __________</span>
        </div>
      </div>
    </div>
  );
}

/* ---------- helpers & shared styles ---------- */
function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

const inputStyle = {
  flex: 1, padding: '12px 14px', border: `1px solid ${C.border}`,
  borderRadius: 10, fontSize: 15, fontFamily: 'inherit',
  color: C.light, background: C.surface, minWidth: 0, outline: 'none',
};

const btnBase = {
  flex: 1, padding: '14px 10px', borderRadius: 12, cursor: 'pointer',
  fontSize: 14, fontWeight: 800, letterSpacing: '.03em',
  textTransform: 'uppercase', fontFamily: 'inherit',
};

const btnPrimary = {
  ...btnBase,
  background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`,
  color: C.bg, border: 'none', boxShadow: `0 2px 12px ${C.gold}44`,
};

const btnSecondary = {
  ...btnBase,
  background: 'transparent', color: C.light, border: `1px solid ${C.border}`,
};

const pCell = {
  border: '1px solid #ccc', padding: '5px 7px', fontSize: 10.5,
};
